import dayjs from 'dayjs';
import { getFaultName } from '@feedflo/fault-codes';
import {
  DATE_FORMAT_MONTH_DAY_YEAR,
  DATE_TIME_FORMAT_MONTH_DAY_YEAR_HOUR_MINUTE_SECOND,
  TIME_FORMAT_HOURS_MINUTES_SECONDS,
} from '../../utils/dates';
import { formatBinSetName } from '../../utils/hooks/useFormattedBinSetName/useFormattedBinSetName';
import { MESSAGE_HISTORY_MODE_DB_TO_UI } from '../../utils/messageHistoryUrl';
import { mapTransportStatus } from '../../utils/transportStatus';

export const PAGE_SIZE = 50;

export const SERIES_OPTIONS = [
  { id: 'mode', name: 'Notification Methods', value: 'mode' },
  { id: 'type', name: 'Type', value: 'type' },
  { id: 'user', name: 'User', value: 'user' },
  { id: 'barn', name: 'Barn', value: 'barn' },
];

export const MODE_FILTER_OPTIONS = [
  { id: 'push', name: 'App' },
  { id: 'email', name: 'Email' },
  { id: 'sms', name: 'SMS' },
];

export const FAULT_TYPE_FILTER_OPTIONS = faultTypeOptionsFromCodes([
  2001, // Empty Pipe (Feed Outage)
  2002, // Auger Running
  2004, // Inactive Auger
  2101, // Bin Slide Open
  2106, // Hard Bin Lockdown
  6000, // Sudden Consumption Drop
  6001, // Consumption Trending Down
  6500, // Sensor Offline
]);

export function sanitizeIdList(values) {
  if (!Array.isArray(values)) {
    return [];
  }

  return values
    .map((value) => {
      if (typeof value === 'string' || typeof value === 'number') {
        return String(value);
      }
      if (value && typeof value === 'object') {
        return value.id || value.value || null;
      }
      return null;
    })
    .filter((value) => value && value !== '[object Object]');
}

export function selectedItemsToIds(selectedItems) {
  return (selectedItems || []).map((item) => item.id || item.value).filter(Boolean);
}

/**
 * Hasura custom SQL functions use Postgres internal array types (_uuid, _text, _int4)
 * rather than GraphQL list types. Values must be Postgres array literal strings, e.g.
 * "{}" or "{uuid1,uuid2}" or "{\"push\",\"sms\"}".
 * @see https://github.com/hasura/graphql-engine/issues/2160
 */
export function toPostgresArrayLiteral(values, { quoteStrings = false } = {}) {
  if (!values?.length) {
    return '{}';
  }

  const formatted = values.map((value) => {
    if (quoteStrings) {
      return `"${String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
    }
    return String(value);
  });

  return `{${formatted.join(',')}}`;
}

export function resolveFilterOptionId(categoryKey, options) {
  const key = String(categoryKey);
  const match = (options || []).find((option) => String(option.id).toLowerCase() === key.toLowerCase());
  return match?.id ?? key;
}

/** Set a single category filter from a chart click; click again to clear. */
export function toggleSingleCategoryFilter(currentValues, categoryKey) {
  const key = String(categoryKey);
  const current = sanitizeIdList(currentValues);
  const isSameSingleSelection =
    current.length === 1 && current[0].toLowerCase() === key.toLowerCase();

  if (isSameSingleSelection) {
    return [];
  }

  return [key];
}

export function faultTypeOptionsFromCodes(codes) {
  return [...new Set(codes.filter((code) => code != null))]
    .map((code) => ({
      id: String(code),
      name: safeGetFaultName(code),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function buildMessageWhereClause({
  selectedOrgIds,
  selectedBarnIds,
  selectedUserIds,
  selectedModes,
  selectedTypes,
  dateFrom,
  dateTo,
}) {
  const where = {
    deleted_at: { _is_null: true },
    sent_at: {
      _gte: dateFrom,
      _lte: dateTo,
    },
    _not: {
      delivery_messages: {},
    },
  };

  if (selectedModes.length > 0) {
    where.transported_as = { _in: selectedModes };
  }

  if (selectedUserIds.length > 0) {
    where.user_id = { _in: selectedUserIds };
  }

  // Always require a visible farm. Hasura applies the `farm` select permission to this nested
  // predicate, so messages whose barns the viewer cannot see are excluded server-side. This keeps
  // limit/offset paging aligned with the rendered rows (we no longer drop whole rows client-side).
  const farmWhere = {
    deleted_at: { _is_null: true },
  };
  if (selectedBarnIds.length > 0) {
    farmWhere.id = { _in: selectedBarnIds };
  }
  if (selectedOrgIds.length > 0) {
    farmWhere.organization_id = { _in: selectedOrgIds };
  }

  const feedLineDevicePath = {
    device_assignments: {
      deleted_at: { _is_null: true },
      ended_at: { _is_null: true },
      feed_line: {
        deleted_at: { _is_null: true },
        farm: farmWhere,
      },
    },
  };

  const binControllerDevicePath = {
    bin_controller: {
      deleted_at: { _is_null: true },
      bin_set: {
        deleted_at: { _is_null: true },
        farm: farmWhere,
      },
    },
  };

  const faultFilter = {
    deleted_at: { _is_null: true },
    device: {
      _or: [feedLineDevicePath, binControllerDevicePath],
    },
  };

  if (selectedTypes.length > 0) {
    faultFilter.code = { _in: selectedTypes.map((t) => parseInt(t, 10)) };
  }

  where.fault_messages = {
    deleted_at: { _is_null: true },
    fault: faultFilter,
  };

  return where;
}

export function extractFarmFromFaultMessage(faultMessage) {
  const assignmentFarm = faultMessage?.fault?.device?.device_assignments?.[0]?.feed_line?.farm;
  if (assignmentFarm) {
    return assignmentFarm;
  }

  return faultMessage?.fault?.device?.bin_controller?.bin_set?.farm || null;
}

export function extractOrganizationIdFromFaultMessage(faultMessage) {
  const farm = extractFarmFromFaultMessage(faultMessage);
  return farm?.organization?.id || null;
}

export function resolveMessageOrganizationId(faultMessages, selectedOrgIds = []) {
  const normalizedSelectedOrgIds = sanitizeIdList(selectedOrgIds);

  if (normalizedSelectedOrgIds.length === 1) {
    return normalizedSelectedOrgIds[0];
  }

  const orgIds = (faultMessages || [])
    .map(extractOrganizationIdFromFaultMessage)
    .filter(Boolean);

  const uniqueOrgIds = [...new Set(orgIds)];

  if (uniqueOrgIds.length === 1) {
    return uniqueOrgIds[0];
  }

  if (uniqueOrgIds.length > 1) {
    return uniqueOrgIds[0];
  }

  return null;
}

export function extractFeedlineOrBinsetFromFaultMessage(faultMessage) {
  const device = faultMessage?.fault?.device;
  const binNames = device?.bin_controller?.bin_controller_bins
    ?.map((bcb) => bcb.bin?.name)
    .filter(Boolean);

  if (binNames?.length) {
    return formatBinSetName(binNames);
  }

  return device?.device_assignments?.[0]?.feed_line?.name || null;
}

export function formatMessageSentDate(sentAt) {
  if (!sentAt) {
    return '—';
  }
  return dayjs.tz(1000 * sentAt).format(DATE_FORMAT_MONTH_DAY_YEAR);
}

export function formatMessageSentTime(sentAt) {
  if (!sentAt) {
    return '—';
  }
  return dayjs.tz(1000 * sentAt).format(TIME_FORMAT_HOURS_MINUTES_SECONDS);
}

export function formatDistinctCellValues(values, formatter = (v) => v) {
  const unique = [...new Set(values.filter(Boolean))];
  if (unique.length === 0) {
    return '—';
  }
  if (unique.length <= 2) {
    return unique.map(formatter).join(', ');
  }
  return `${formatter(unique[0])} +${unique.length - 1}`;
}

export function safeGetFaultName(code) {
  try {
    return getFaultName(code);
  } catch (e) {
    return String(code);
  }
}

export function formatReason(reason) {
  if (!reason) {
    return '—';
  }
  return reason.charAt(0).toUpperCase() + reason.slice(1);
}

export function formatMode(transportedAs) {
  return MESSAGE_HISTORY_MODE_DB_TO_UI[transportedAs] || transportedAs;
}

export const STATUS_CONFIG = {
  delivered: { label: 'Delivered', variant: 'delivered' },
  failed: { label: 'Failed', variant: 'failed' },
  pending: { label: 'Pending', variant: 'pending' },
  unsubscribed: { label: 'User unsubscribed', variant: 'unsubscribed' },
};

const NORMALIZED_STATUS_TO_UI_CATEGORY = {
  delivered: 'success',
  failed: 'error',
  pending: 'pending',
  unsubscribed: 'unsubscribed',
};

function pickStatusDetailMatchingNormalizedStatus(transportType, statuses, normalizedStatus) {
  const targetCategory = NORMALIZED_STATUS_TO_UI_CATEGORY[normalizedStatus];
  if (!targetCategory || !statuses?.length) {
    return null;
  }

  const matching = statuses.filter(
    (status) => mapTransportStatus(transportType, status.status) === targetCategory,
  );

  if (!matching.length) {
    return null;
  }

  return matching.reduce((best, current) => {
    const bestAt = best.occurred_at ?? 0;
    const currentAt = current.occurred_at ?? 0;
    return currentAt > bestAt ? current : best;
  });
}

export function buildMessageStatus(message) {
  const normalized = message.status || null;
  const config = STATUS_CONFIG[normalized] || null;

  let timestamp = null;
  let detail = null;

  if (message.transported_as === 'sms') {
    const matchingStatus = pickStatusDetailMatchingNormalizedStatus(
      'sms',
      message.sms_notification_statuses,
      normalized,
    );
    if (matchingStatus?.occurred_at) {
      timestamp = dayjs.tz(1000 * matchingStatus.occurred_at).format(DATE_TIME_FORMAT_MONTH_DAY_YEAR_HOUR_MINUTE_SECOND);
    }
  } else if (message.transported_as === 'email') {
    const matchingStatus = pickStatusDetailMatchingNormalizedStatus(
      'email',
      message.email_notification_statuses,
      normalized,
    );
    if (matchingStatus?.occurred_at) {
      timestamp = dayjs.tz(1000 * matchingStatus.occurred_at).format(DATE_TIME_FORMAT_MONTH_DAY_YEAR_HOUR_MINUTE_SECOND);
    }
    if (normalized === 'failed' && matchingStatus?.data) {
      const errorText = typeof matchingStatus.data === 'string' ? matchingStatus.data : matchingStatus.data?.reject_reason || matchingStatus.data?.description;
      if (errorText) {
        detail = String(errorText);
      }
    }
  } else if (message.transported_as === 'push') {
    const pushStatuses = (message.push_notification_receipts || []).map((receipt) => ({
      status: receipt.receipt_status,
      occurred_at: receipt.receipt_completed_at,
      receipt_message: receipt.receipt_message,
    }));
    const matchingStatus = pickStatusDetailMatchingNormalizedStatus('push', pushStatuses, normalized);
    if (matchingStatus?.occurred_at) {
      timestamp = dayjs.tz(1000 * matchingStatus.occurred_at).format(DATE_TIME_FORMAT_MONTH_DAY_YEAR_HOUR_MINUTE_SECOND);
    }
    if (normalized === 'failed' && matchingStatus?.receipt_message) {
      detail = matchingStatus.receipt_message;
    }
  }

  return {
    status: normalized,
    label: config?.label || null,
    variant: config?.variant || null,
    timestamp,
    detail,
  };
}

export function messageToTableRowData(message, { selectedOrgIds = [] } = {}) {
  const faultMessages = message.fault_messages || [];

  const entries = faultMessages
    .map((faultMessage) => {
      const farm = extractFarmFromFaultMessage(faultMessage);
      if (!farm?.id) {
        return null;
      }

      return {
        barn: farm.name || '—',
        barnId: farm.id,
        feedline: extractFeedlineOrBinsetFromFaultMessage(faultMessage) || '—',
        type: safeGetFaultName(faultMessage.fault?.code),
        reason: formatReason(faultMessage.reason),
        faultId: faultMessage.fault?.id || null,
      };
    })
    .filter(Boolean);

  if (entries.length === 0) {
    return null;
  }

  return {
    id: message.id,
    date: formatMessageSentDate(message.sent_at),
    time: formatMessageSentTime(message.sent_at),
    userId: message.user?.id || null,
    userName: message.user?.name || '—',
    organizationId: resolveMessageOrganizationId(faultMessages, selectedOrgIds),
    transportedAs: message.transported_as,
    status: buildMessageStatus(message),
    entries,
  };
}

export function pivotChartRows(rows, seriesBy) {
  const bucketMap = new Map();
  const keyNameMap = {};
  const keysSet = new Set();

  rows.forEach((row) => {
    const dateKey = dayjs.unix(row.bucket_start).format('YYYY-MM-DD');
    if (!bucketMap.has(dateKey)) {
      bucketMap.set(dateKey, { date: dateKey });
    }
    const bucket = bucketMap.get(dateKey);
    bucket[row.series_key] = row.message_count;
    keysSet.add(row.series_key);

    if (row.series_label) {
      keyNameMap[row.series_key] = row.series_label;
    } else if (seriesBy === 'mode') {
      keyNameMap[row.series_key] = formatMode(row.series_key);
    } else if (seriesBy === 'type') {
      keyNameMap[row.series_key] = safeGetFaultName(parseInt(row.series_key, 10));
    } else {
      keyNameMap[row.series_key] = row.series_key;
    }
  });

  const keys = [...keysSet];
  const data = [...bucketMap.values()].sort((a, b) => a.date.localeCompare(b.date));
  const yAxisValues = data.map((d) =>
    keys.reduce((sum, key) => sum + (d[key] || 0), 0),
  );

  return {
    data,
    keys,
    keyNameMap,
    yAxisValues: yAxisValues.length > 0 ? yAxisValues : [0],
  };
}

export function isChartDataEmpty(chartPivot) {
  if (!chartPivot?.data?.length) {
    return true;
  }

  return chartPivot.yAxisValues.every((value) => value === 0);
}

export function resolveDateRange(fromParam, toParam) {
  if (fromParam && toParam) {
    return {
      from: dayjs.unix(fromParam).startOf('day'),
      to: dayjs.unix(toParam).endOf('day'),
    };
  }

  return {
    from: dayjs.tz().subtract(30, 'days').startOf('day'),
    to: dayjs.tz().endOf('day'),
  };
}
